/**
 * Web StorageAdapter implementation.
 *
 * Delegates to IndexedDB (per-doc and per-user CRDT caches), localStorage
 * (UI prefs), and `window.location` (WebSocket URL construction).
 *
 * User indexes use guarded, versioned transactions; document caches retain
 * y-indexeddb persistence. See docs/architecture/storage-adapter.md for the
 * contract this adapter implements.
 */

import { DocumentPersistence, VaultPersistence, discardDocPersistence } from "./web-persistence.js";
import { accountCacheNames } from "./account-cache.js";
export { flushPendingWrites, reloadApp } from "./web-persistence.js";
export {
  putStoredAttachment,
  getStoredAttachment,
  deleteStoredAttachment,
  listStoredAttachments,
  deleteStoredMapAttachments,
  listStoredAttachmentMaps,
} from "./web-attachment-store.js";

// Broad cleanup is reserved for explicit account deletion. Ordinary logout
// uses clearAccountCaches and never enumerates or clears local document data.
const DB_PATTERNS = [
  /^orbital-/,
  /^yjs-/,
  /^crdt-/,
  /^mindmap-sync-/,
];

const PREF_PREFIXES = [
  "orbital-",
  "orbital_",
  "project-planner-", // legacy theme key
];

// ============================================
// CRDT cache (Yjs persistence providers)
// ============================================

export function createDocPersistence(docId, doc, _options = {}) {
  return new DocumentPersistence(`orbital-${docId}`, doc);
}

export function createUserPersistence(userId, doc) {
  return new VaultPersistence(userId, doc);
}

/**
 * Delete a single doc's local CRDT cache. Resolves on success and on
 * "database does not exist" (deleteDatabase succeeds in both cases per the
 * IndexedDB spec). Resolves on `blocked` events too, since another tab
 * holding the connection is not an error worth aborting cleanup for —
 * matches the pre-existing purgeLinkedDoc behavior.
 */
export function deleteDocCache(docId) {
  // A provider still open on this doc (e.g. the revoked map that is still on
  // screen) must not try to save into the database being deleted.
  discardDocPersistence(`orbital-${docId}`);
  return new Promise((resolve, reject) => {
    let req;
    try {
      req = indexedDB.deleteDatabase(`orbital-${docId}`);
    } catch (err) {
      reject(err);
      return;
    }
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
    req.onblocked = () => resolve();
  });
}

/**
 * Delete only known account caches. Keep device-local documents, preferences
 * and unclassified recovery sources; never infer ownership from a name prefix.
 */
export async function clearAccountCaches(scope) {
  let docs = 0;
  for (const name of accountCacheNames(scope)) {
    await new Promise((resolve, reject) => {
      const request = indexedDB.deleteDatabase(name);
      request.onsuccess = resolve;
      request.onerror = () => reject(request.error);
      request.onblocked = () => reject(new Error('Account cache is still open in another tab'));
    });
    docs++;
  }
  // Device identity, local map indexes, attachments of maps on this device,
  // preferences and unrelated recovery databases are not account data. No
  // database enumeration is needed.
  return { docs, prefs: 0, enumerated: true };
}

export async function clearAllCaches() {
  let docs = 0;
  let prefs = 0;

  // CRDT caches via IndexedDB.databases() when available.
  try {
    if (typeof indexedDB.databases === "function") {
      const databases = await indexedDB.databases();
      const matching = databases.filter(
        (db) => db.name && DB_PATTERNS.some((p) => p.test(db.name)) &&
          // Retain old-client sources: a stale tab may still have unsynced data.
          !(db.name.startsWith("orbital-user-") && !db.name.startsWith("orbital-user-v2-")),
      );
      for (const db of matching) {
        try {
          indexedDB.deleteDatabase(db.name);
          docs++;
        } catch {
          /* ignore individual failures */
        }
      }
    }
    // If indexedDB.databases() is missing (Firefox), callers that know
    // specific IDs (e.g., the active doc) should call deleteDocCache()
    // directly. clearAllCaches() can't enumerate without that API.
  } catch {
    /* ignore — falls through to KV cleanup */
  }

  // KV prefs via prefix scan over localStorage.
  try {
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && PREF_PREFIXES.some((p) => key.startsWith(p))) {
        toRemove.push(key);
      }
    }
    for (const key of toRemove) {
      localStorage.removeItem(key);
      prefs++;
    }
  } catch {
    /* ignore */
  }

  return { docs, prefs };
}

// ============================================
// Sync transport endpoints
// ============================================

export function getSyncUrl(path) {
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}${path}`;
}

export function isSyncAvailable() {
  return true;
}

// ============================================
// Key-value preferences (localStorage-backed)
// ============================================

export function getPref(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function setPref(key, value) {
  // No try/catch — quota errors must propagate so callers can route them
  // through StorageHealth.isQuotaError() and emit STORAGE_WARNING. See
  // shared/local-doc-state.js for the established pattern.
  localStorage.setItem(key, value);
}

export function removePref(key) {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export function clearPrefsByPrefix(prefix) {
  try {
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) toRemove.push(key);
    }
    for (const key of toRemove) localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** Read only the locally stored CRDT byte lengths; never create a cache for
 * an absent document and never trigger Yjs persistence/compaction while sizing. */
export async function readDocSize(docId) {
  if (typeof indexedDB === 'undefined') return null;
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(`orbital-${docId}`);
    request.onupgradeneeded = () => { request.transaction.abort(); };
    request.onblocked = () => reject(new Error('Local storage is busy'));
    request.onerror = () => request.error?.name === 'AbortError' ? resolve(null) : reject(request.error);
    request.onsuccess = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains('updates')) { db.close(); resolve(null); return; }
      let size = 0;
      const tx = db.transaction('updates', 'readonly');
      const cursor = tx.objectStore('updates').openCursor();
      cursor.onsuccess = () => {
        const entry = cursor.result;
        if (!entry) return;
        size += entry.value?.byteLength || 0;
        entry.continue();
      };
      tx.oncomplete = () => { db.close(); resolve(size); };
      tx.onerror = () => { db.close(); reject(tx.error); };
      tx.onabort = () => { db.close(); reject(tx.error || new Error('Local size read interrupted')); };
    };
  });
}
